import {
  CommentaryV2,
  estimatedDemandSupplyOptions,
  estimatedExposureTimeOptions,
  expected,
  marketConditionsCommentaryFilterTypes,
  schMarketConditionsCommentaryFilterType,
} from '@aloft/toolkit-shared';
import { Button, Flex, Input, RadioGroup, Select, Typography } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';

import { FormTextarea } from '../../../components/FormTextarea/FormTextarea.tsx';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { selectMarketAggregateData } from '../../../redux/features/selectors/commentarySelectors/selectCurrentMarketAggregateData.ts';
import { selectOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectBackupListingDataById } from '../../../redux/features/selectors/propertyDataSelectors/backupListingDataSelectors.ts';
import { selectPropertyIdentificationById } from '../../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { MarketAdjustments } from '../../commentary/MarketAdjustments/MarketAdjustments.tsx';
import { MarketConditionsGraphs } from '../components/MarketConditionsGraphs.tsx';
import { getGeoIdForSelectedGeoType } from '../utils/getGeoIdForSelectedGeoType.ts';
import { parseMarketConditionsGeoType } from '../utils/parseMarketConditionsGeoType.ts';

export const MarketConditionsTabPanels = () => null;

MarketConditionsTabPanels.LeftPanel = () => {
  const { setValue, watch, control } = useFormContext<CommentaryV2>();
  const propertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const { errors } = useAppraisifyValidation();
  const { marketConditionCommentary } = errors?.Commentary ?? undefined;
  const commentaryFieldName: FieldPath<CommentaryV2> = 'marketConditionsV2.commentary';
  const estimatedDemandSupplyFieldName: FieldPath<CommentaryV2> =
    'marketConditionsV2.estimatedDemandSupply';
  const estimatedExposureTimeFieldName: FieldPath<CommentaryV2> =
    'marketConditionsV2.estimatedExposureTime';
  const filterTypeFieldName: FieldPath<CommentaryV2> = 'marketConditionsV2.filter.type';
  const filterValueFieldName: FieldPath<CommentaryV2> = 'marketConditionsV2.filter.value';

  const estimatedDemandSupply = watch(estimatedDemandSupplyFieldName);
  const estimatedExposureTime = watch(estimatedExposureTimeFieldName);
  const filterType = watch(filterTypeFieldName);
  const filterValue = watch(filterValueFieldName);
  const [zipCodeValue, setZipCodeValue] = useState<string>(
    filterValue ?? propertyIdentification?.address.PostalCode?.substring(0, 5) ?? '',
  );

  const onApplyZipCodeValue = () => {
    setValue(filterValueFieldName, zipCodeValue);
  };

  return (
    <Flex flexDirection="column" padding="1rem" gap="1.5rem" width="100%">
      <Typography variant="h2">Market Condition Analysis</Typography>
      <Flex flexDirection="column" gap="1rem">
        <Flex gap="2rem">
          <Flex whiteSpace="nowrap">
            <RadioGroup
              isRequired
              size="md"
              label="Filter By:"
              items={marketConditionsCommentaryFilterTypes.map((value) => ({
                label: value,
                value,
              }))}
              value={filterType}
              onChange={(v) =>
                setValue(filterTypeFieldName, schMarketConditionsCommentaryFilterType.parse(v))
              }
            />
          </Flex>
          <Flex flexGrow={1} alignItems="center" gap="0.5rem">
            <Input
              type="text"
              size="xs"
              value={zipCodeValue}
              onChange={(v) => setZipCodeValue(v)}
              onClear={() => setZipCodeValue('')}
            />
            <Button
              variant="primary"
              size="sm"
              text="Apply"
              disabled={zipCodeValue.length !== 5 || zipCodeValue === filterValue}
              onClick={onApplyZipCodeValue}
            />
          </Flex>
        </Flex>
      </Flex>
      <Select
        isRequired
        label="Estimated Demand / Supply"
        value={estimatedDemandSupply}
        options={estimatedDemandSupplyOptions.map((value) => ({ label: value, value }))}
        onChange={(v) => setValue(estimatedDemandSupplyFieldName, expected(v))}
      />
      <Select
        isRequired
        label="Estimated Exposure Time"
        value={estimatedExposureTime}
        infoText="The amount of time the property would have been exposed to the open market before a buyer and seller, each reasonably motivated and well-informed, agreed on the appraised market value. It assumes normal marketing efforts and typical market conditions prevailing during that period."
        options={estimatedExposureTimeOptions.map((value) => ({ label: value, value }))}
        onChange={(v) => setValue(estimatedExposureTimeFieldName, expected(v))}
      />
      <FormTextarea
        name={commentaryFieldName}
        control={control}
        label="Market Condition Commentary"
        placeholder=""
        isRequired
        column
        resize="vertical"
        customErrorMessage={marketConditionCommentary?.message}
        onClear={() => setValue(commentaryFieldName, undefined)}
        labelVariant="label"
        labelColor="textPrimary"
      />
    </Flex>
  );
};

MarketConditionsTabPanels.RightPanel = () => {
  const { id } = useAppSelector(selectOrder);
  const propertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const readOnlyListingData = useAppSelector((state) =>
    selectBackupListingDataById(state, 'subject'),
  );
  const { watch } = useFormContext<CommentaryV2>();
  const filterTypeFieldName: FieldPath<CommentaryV2> = 'marketConditionsV2.filter.type';
  const filterValueFieldName: FieldPath<CommentaryV2> = 'marketConditionsV2.filter.value';
  const filterType = watch(filterTypeFieldName);
  const filterValue = watch(filterValueFieldName);
  const parsedGeoType = parseMarketConditionsGeoType(filterType);
  const parsedGeoId = getGeoIdForSelectedGeoType(
    parsedGeoType,
    filterValue,
    propertyIdentification,
    readOnlyListingData,
  );
  const allMarketAggregateData = useAppSelector((state) => selectMarketAggregateData(state));

  return (
    <>
      <MarketConditionsGraphs
        orderID={id}
        geoType={parsedGeoType}
        geoId={parsedGeoId}
        allMarketAggregateData={allMarketAggregateData}
      />
      <MarketAdjustments title="market-condition" />
    </>
  );
};
